import * as React from 'react'
import { useMutationState } from '@tanstack/react-query'
import { useCookies } from 'react-cookie'
import { CurrencyCode } from '@repo/graphql-types'
import { useUpdateUserCurrencyMutation } from 'codegen/generated/user-preferences'
import { CURRENCY_COOKIE_NAME } from 'constants/cookies'
import { sessionStore } from 'utils/storage'
import { useUserQuery } from './auth'
import { isLoggedInQuerySelector } from '../selectors'
import { writeCurrencyPreferenceCookie } from './write-currency-preference-cookie'

const RELOAD_STORAGE_KEY = 'cf-currency-reload-pending'

export interface UseSyncCurrencyCookieOptions {
  /** Runs after the cookie was updated to match the account currency. */
  onSync?: () => void
}

export function useSyncCurrencyCookie(options: UseSyncCurrencyCookieOptions = {}) {
  const { onSync = () => window.location.reload() } = options
  const {
    data: accountCurrency,
    isSuccess,
    isFetching,
  } = useUserQuery(undefined, {
    select: data =>
      !data.me?.errors?.length && isLoggedInQuerySelector(data)
        ? data.me?.user?.currency.code
        : undefined,
  })
  const [cookies, setCookie, removeCookie] = useCookies([CURRENCY_COOKIE_NAME])
  const currency = cookies[CURRENCY_COOKIE_NAME]
  const currencyUpdates = useMutationState({
    filters: { mutationKey: useUpdateUserCurrencyMutation.getKey() },
    select: mutation => mutation.state.status,
  })
  const isChangingCurrency = currencyUpdates.some(
    status => status === 'pending' || status === 'success',
  )
  const hasReconciled = React.useRef(false)

  React.useEffect(() => {
    // Currency selectors reload after saving; their cached user response may still have the old currency.
    if (
      !isSuccess ||
      isFetching ||
      isChangingCurrency ||
      hasReconciled.current ||
      !accountCurrency
    ) {
      return
    }

    if (!Object.values(CurrencyCode).includes(accountCurrency)) {
      return
    }

    if (typeof currency === 'string' && currency.toLowerCase() === accountCurrency.toLowerCase()) {
      hasReconciled.current = true
      sessionStore.remove(RELOAD_STORAGE_KEY)
      return
    }

    if (
      !writeCurrencyPreferenceCookie(accountCurrency, {
        hostname: window.location.hostname,
        setCookie,
        removeCookie,
      })
    ) {
      hasReconciled.current = true
      return
    }

    hasReconciled.current = true

    // This marker survives reloads until a page load confirms the currencies agree.
    if (sessionStore.getString(RELOAD_STORAGE_KEY)) {
      return
    }
    if (!sessionStore.setString(RELOAD_STORAGE_KEY, '1')) {
      return
    }

    onSync()
  }, [
    accountCurrency,
    currency,
    isSuccess,
    isFetching,
    isChangingCurrency,
    onSync,
    setCookie,
    removeCookie,
  ])
}
